import React, { FC, useMemo } from "react";
import { Badge, Modal, Table } from "react-bootstrap";
import {
    Build,
    Localization,
    Network,
    Revision,
    Variation,
    VariationSet
} from "../../models/types";
import styles from "./BuildOverviewModal.module.scss";

interface Props {
    revision: Revision;
    onClose: () => void;
}

interface BuildMatrixRow {
    buildName: string;
    buildDescription?: string;
    variationName: string;
    variationDescription?: string;
    network: string;
    localization: string;
    size: number;
    duration: string;
}

export const BuildOverviewModal: FC<Props> = ({ revision, onClose }) => {
    const buildData = revision.buildData;
    const setBlacklist = revision.revisionConfig?.setBlacklist;

    const { matrixRows, hasLocalizations, hasVariations } = useMemo(() => {
        if (!buildData) {
            return {
                matrixRows: [],
                hasLocalizations: false,
                hasVariations: false
            };
        }

        const builds: Build[] =
            buildData.builds?.filter((b: Build) => b.include) || [];
        const networks: Network[] = (buildData.networks || []).filter(
            (n: Network) => n.sizeTargets.length > 0
        );
        const localizations: Localization[] =
            buildData.localizations?.filter((l: Localization) => l.include) ||
            [];
        const blacklist = setBlacklist || [];
        const variations: Variation[] =
            buildData.variations?.filter(
                (v: Variation) =>
                    v.include &&
                    !v.sets?.some((s: VariationSet) =>
                        blacklist.includes(s.set)
                    )
            ) || [];
        const variationSetsWithDesc = variations.flatMap(v =>
            (v.sets || []).map(s => ({
                varSet: s,
                description: v.description
            }))
        );

        const rows: BuildMatrixRow[] = [];

        const addRows = (
            build: Build,
            variationName: string,
            variationDescription?: string
        ) => {
            const compatibleNetworks = networks.filter(net =>
                net.sizeTargets.includes(build.size)
            );
            const localizationNames =
                localizations.length > 0
                    ? localizations.map(loc => loc.name).join(", ")
                    : "-";

            for (const network of compatibleNetworks) {
                rows.push({
                    buildName: build.name,
                    buildDescription: build.description || undefined,
                    variationName,
                    variationDescription,
                    network: network.nameOverride || network.network,
                    localization: localizationNames,
                    size: build.size,
                    duration: build.duration
                });
            }
        };

        for (const build of builds) {
            const isBlacklisted = build.sets?.some(set =>
                blacklist.includes(set)
            );
            if (isBlacklisted) continue;

            if (variationSetsWithDesc.length > 0) {
                for (const { varSet, description } of variationSetsWithDesc) {
                    addRows(build, varSet.name, description || undefined);
                }
            } else {
                addRows(build, "-");
            }
        }

        return {
            matrixRows: rows,
            hasLocalizations: localizations.length > 0,
            hasVariations: variationSetsWithDesc.length > 0
        };
    }, [buildData, setBlacklist]);

    const sizeBadgeVariant = (size: number) => {
        switch (size) {
            case 2:
                return "density-1";
            case 3:
                return "density-2";
            case 5:
                return "density-3";
            default:
                return "primary";
        }
    };

    const durationBadgeVariant = (duration: string) => {
        switch (duration) {
            case "15s":
                return "info";
            case "30s":
                return "info-2";
            default:
                return "primary";
        }
    };

    return (
        <Modal show={true} onHide={onClose} keyboard={true} size="xl" centered>
            <Modal.Header closeButton>
                <Modal.Title as="h5">Build Overview</Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.body}>
                {matrixRows.length === 0 ? (
                    <p className="text-center text-muted py-3">
                        No builds configured.
                    </p>
                ) : (
                    <Table
                        hover
                        responsive
                        className={styles.buildTable}
                        size="sm"
                    >
                        <thead>
                            <tr className={styles.tableHeader}>
                                <th>Base Version</th>
                                {hasVariations && <th>Variation</th>}
                                <th>Network</th>
                                {hasLocalizations && <th>Localization</th>}
                                <th>Size</th>
                                <th>Duration</th>
                            </tr>
                        </thead>
                        <tbody>
                            {matrixRows.map((row, index) => (
                                <tr key={index}>
                                    <td>
                                        {row.buildName}
                                        {row.buildDescription && (
                                            <div className={styles.description}>
                                                {row.buildDescription}
                                            </div>
                                        )}
                                    </td>
                                    {hasVariations && (
                                        <td>
                                            {row.variationName}
                                            {row.variationDescription && (
                                                <div
                                                    className={
                                                        styles.description
                                                    }
                                                >
                                                    {row.variationDescription}
                                                </div>
                                            )}
                                        </td>
                                    )}
                                    <td>{row.network}</td>
                                    {hasLocalizations && (
                                        <td>{row.localization}</td>
                                    )}
                                    <td>
                                        <Badge
                                            bg={sizeBadgeVariant(row.size)}
                                            className="text-white"
                                        >
                                            {row.size} MB
                                        </Badge>
                                    </td>
                                    <td>
                                        <Badge
                                            bg={durationBadgeVariant(
                                                row.duration
                                            )}
                                            className="text-white"
                                        >
                                            {row.duration}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Modal.Body>
        </Modal>
    );
};
